// Client for the platform operations console (/admin). Every call is
// network-only: staff decisions must never be made on cached data.
import {
  adminConsoleRequest as request,
  type AccountDeletionRequestStatus,
  type AdminAuditEventSummary,
  type AdminVenueReview,
  type ProviderApprovalStatus,
  type UserRole,
  type VenueApprovalStatus,
  type VenueReviewStatus,
} from "./api";
import type {
  PromotionConfiguration,
  PromotionRecord,
} from "../features/admin-console/promotions/types";

export type CommissionBase = "session_time" | "food" | "total";
export type SettlementPaymentMethod = "transfer" | "cash" | "instapay" | "wallet" | "other";
export type NoteEntityType = "provider" | "venue" | "user";
export type MembershipRole = "owner" | "manager" | "front_desk";

export interface ConsoleSearchHit {
  kind: "provider" | "venue" | "account";
  id: string;
  title: string;
  subtitle: string | null;
  status: string | null;
}

export interface ConsoleSearchResponse {
  query: string;
  providers: ConsoleSearchHit[];
  venues: ConsoleSearchHit[];
  accounts: ConsoleSearchHit[];
}

export interface ConsoleQueueCount {
  key: "provider_registrations" | "venue_additions" | "venue_removals" | "tournaments" | "reviews" | "reports" | "deletion_requests";
  count: number;
  oldest_at: string | null;
}

export type TournamentReviewStatus = "pending" | "approved" | "rejected";

export interface ConsoleTournament {
  id: string;
  venue_id: string;
  venue_name: string;
  venue_city: string;
  venue_timezone: string;
  provider_id: string;
  provider_name: string;
  name: string;
  game: string;
  description: string | null;
  rules: string | null;
  registration_instructions: string | null;
  contact_phone: string | null;
  starts_at: string;
  ends_at: string;
  status: "draft" | "published" | "cancelled";
  review_status: TournamentReviewStatus | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  updated_at: string;
  created_by_name: string | null;
  reviewed_by_name: string | null;
  last_review_note: string | null;
}

export interface ConsoleHomeResponse {
  queues: ConsoleQueueCount[];
  overdue_settlements: number;
  venues_without_commission: number;
}

export interface ConsoleAccountSummary {
  id: string;
  email: string;
  display_name: string | null;
  phone: string | null;
  role: UserRole;
  is_active: boolean;
  email_verified: boolean;
  setup_pending: boolean;
  created_at: string;
  provider_id: string | null;
  provider_name: string | null;
}

export interface ConsoleReview {
  id: string;
  venue_id: string;
  venue_name: string;
  customer_user_id: string | null;
  customer_name: string | null;
  stars: number;
  comment: string | null;
  status: VenueReviewStatus;
  review_note: string | null;
  created_at: string;
  reviewed_at: string | null;
}

export interface ConsoleAccountDetail extends ConsoleAccountSummary {
  active_session_count: number;
  last_sign_in_at: string | null;
  memberships: Array<{
    provider_id: string;
    provider_name: string;
    provider_status: ProviderApprovalStatus;
    membership_role: MembershipRole;
    venue_ids: string[] | null;
  }>;
  deletion_requests: Array<{
    id: string;
    status: AccountDeletionRequestStatus;
    reason: string | null;
    admin_note: string | null;
    requested_at: string;
    reviewed_at: string | null;
    completed_at: string | null;
  }>;
  reviews: ConsoleReview[];
  reports_filed: number;
  reports_against: number;
  is_platform_staff: boolean;
}

export interface ConsoleAccountActionResult {
  account: ConsoleAccountDetail;
  message: string;
  email_sent: boolean | null;
}

export interface ConsoleCommissionRule {
  id: string;
  venue_id: string;
  base: CommissionBase;
  percent: string;
  effective_from: string;
  effective_to: string | null;
  note: string | null;
  created_at: string;
  created_by_name: string | null;
  is_current: boolean;
}

export interface ConsoleVenueDetail {
  id: string;
  name: string;
  city: string;
  address: string;
  timezone: string;
  provider_id: string;
  provider_name: string;
  provider_status: ProviderApprovalStatus;
  approval_status: VenueApprovalStatus;
  is_active: boolean;
  latitude: number | null;
  longitude: number | null;
  image_url: string | null;
  logo_url: string | null;
  submission_note: string | null;
  removal_note: string | null;
  review_note: string | null;
  reviewed_at: string | null;
  created_at: string;
  visibility_checks: Record<string, boolean>;
  is_publicly_visible: boolean;
  units: Array<{ id: string; name: string; type: string; status: string; base_hourly_rate: number; capacity: number; is_vip: boolean }>;
  operating_hours: Array<{ day_of_week: number; is_open: boolean; open_time: string | null; close_time: string | null }>;
  upcoming_blackouts: Array<{ id: string; start_at: string; end_at: string; note: string | null }>;
  review_counts: Record<string, number>;
  average_stars: number | null;
  commission_rules: ConsoleCommissionRule[];
}

export interface ConsoleVenueRow {
  id: string;
  name: string;
  city: string;
  provider_id: string;
  provider_name: string;
  approval_status: VenueApprovalStatus;
  is_active: boolean;
  created_at: string;
  has_commission_rules: boolean;
}

export interface SettlementRuleLine {
  rule_id: string;
  base: CommissionBase;
  percent: string;
  effective_from: string;
  effective_to: string | null;
  session_count: number;
  base_amount: string;
  commission_amount: string;
}

export interface SettlementVenueLine {
  venue_id: string;
  venue_name: string;
  session_count: number;
  sessions_without_rule: number;
  session_time_amount: string;
  food_amount: string;
  commission_amount: string;
  has_rules: boolean;
  rules: SettlementRuleLine[];
}

export interface SettlementComputation {
  provider_id: string;
  period_start: string;
  period_end: string;
  currency: string;
  session_time_amount: string;
  food_amount: string;
  commission_amount: string;
  venues: SettlementVenueLine[];
}

export interface SettlementStatementRecord {
  id: string;
  provider_id: string;
  period_start: string;
  period_end: string;
  currency: string;
  session_time_amount: string;
  food_amount: string;
  commission_amount: string;
  breakdown: SettlementComputation;
  status: "issued" | "void";
  due_date: string | null;
  note: string | null;
  issued_at: string;
  issued_by_name: string | null;
  voided_at: string | null;
  void_reason: string | null;
  paid_amount: string;
}

export interface SettlementPaymentRecord {
  id: string;
  provider_id: string;
  statement_id: string | null;
  amount: string;
  paid_on: string;
  method: SettlementPaymentMethod;
  reference: string | null;
  note: string | null;
  recorded_by_name: string | null;
  created_at: string;
}

export interface SettlementBalance {
  issued_amount: string;
  paid_amount: string;
  balance_amount: string;
  overdue_amount: string;
  last_payment_on: string | null;
}

export interface ProviderSettlementResponse {
  provider_id: string;
  provider_name: string;
  period_start: string;
  period_end: string;
  preview: SettlementComputation;
  period_statement: SettlementStatementRecord | null;
  statements: SettlementStatementRecord[];
  payments: SettlementPaymentRecord[];
  balance: SettlementBalance;
  venues_without_rules: string[];
}

export interface SettlementOverviewRow {
  provider_id: string;
  provider_name: string;
  provider_status: ProviderApprovalStatus;
  venue_count: number;
  venues_without_rules: number;
  period_commission_amount: string;
  period_statement_id: string | null;
  balance: SettlementBalance;
}

export interface SettlementOverviewResponse {
  period_start: string;
  period_end: string;
  rows: SettlementOverviewRow[];
  totals: { period_commission_amount: string; balance_amount: string; overdue_amount: string };
}

export interface ConsoleNote {
  id: string;
  entity_type: NoteEntityType;
  entity_id: string;
  body: string;
  author_user_id: string | null;
  author_name: string | null;
  created_at: string;
}

export interface AdminAuditPage {
  events: AdminAuditEventSummary[];
  next_cursor: string | null;
}

export interface AdminAuditFilters {
  entity_type?: string;
  entity_id?: string;
  provider_id?: string;
  venue_id?: string;
  action?: string;
  actor?: string;
  from_date?: string;
  to_date?: string;
  cursor?: string | null;
  limit?: number;
}

export interface AdminTimeseries {
  metric: string;
  range: string;
  buckets: Array<{ date: string; value: number }>;
}

export interface AdminRanking {
  dimension: string;
  items: Array<{ id: string; name: string; value: number }>;
}

export interface AdminFunnel {
  from_date: string;
  to_date: string;
  stages: Array<{ name: string; count: number; rate: number | null }>;
}

/**
 * Booking promotion campaign contracts. Shapes mirror the persisted API
 * contract in apps/api/app/promotion_schemas.py (authoritative) and the
 * presentational view-model in features/admin-console/promotions/types.ts.
 * Money stays decimal strings; the server owns all math.
 */

/** One redemption row as the server returns it. */
export interface BookingPromotionRedemption {
  id: string;
  booking_id: string;
  venue_id: string;
  revision_id: string;
  status: "reserved" | "released" | "consumed";
  reserved_credit_amount: string;
  reserved_gross_commission_amount: string;
  final_credit_amount: string | null;
  final_gross_commission_amount: string | null;
  reserved_at: string;
  released_at: string | null;
  consumed_at: string | null;
}

export interface BookingPromotionRedemptionsResponse {
  redemptions: BookingPromotionRedemption[];
  next_cursor: string | null;
}

export interface BookingPromotionRedemptionFilters {
  cursor?: string | null;
  limit?: number;
}

/** Revision payload: full configuration plus the revision being replaced. */
export type BookingPromotionRevisionWrite = PromotionConfiguration & {
  expected_revision_id: string;
};

export type ConsoleAnalyticsRange = "7d" | "30d" | "90d" | "all";

/**
 * One hourly lazy snapshot backing the whole analytics page. generated_at is
 * the single cutoff every panel shares; expires_at tells staff when it goes
 * stale. from_date is null for "all" (true full history, no fabricated epoch).
 */
export interface ConsoleAnalyticsReport {
  range: ConsoleAnalyticsRange;
  from_date: string | null;
  to_date: string;
  generated_at: string;
  expires_at: string;
  totals: {
    approved_providers: number;
    approved_venues: number;
    today_bookings: number;
    active_sessions: number;
    suspended_providers: number;
  };
  trends: AdminTimeseries[];
  rankings: {
    venues: AdminRanking;
    providers: AdminRanking;
  };
  funnel: AdminFunnel;
}

export interface SettlementPeriod {
  period_start?: string;
  period_end?: string;
}

export type ConsoleActivityKind = "all" | "booking" | "session";

export type ConsoleActivityStatus =
  | "pending"
  | "confirmed"
  | "checked_in"
  | "completed"
  | "cancelled"
  | "no_show"
  | "rejected"
  | "expired"
  | "active";

export type ConsoleAttentionReason = "pending_booking" | "overdue_booking" | "overdue_session";

export interface ConsoleActivityOwnerContact {
  user_id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
}

export interface ConsoleActivityItem {
  kind: "booking" | "session";
  id: string;
  status: string;
  source: string;
  booking_id: string | null;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  venue_id: string;
  venue_name: string;
  venue_city: string;
  venue_timezone: string;
  provider_id: string;
  provider_name: string;
  provider_contact_email: string | null;
  provider_contact_phone: string | null;
  unit_id: string;
  unit_name: string;
  owner_contacts: ConsoleActivityOwnerContact[];
  start_at: string;
  end_at: string | null;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
  attention_reason: ConsoleAttentionReason | string | null;
  is_demo: boolean;
}

export interface ConsoleActivityResponse {
  items: ConsoleActivityItem[];
  total: number;
  limit: number;
  offset: number;
  generated_at: string;
}

export interface ConsoleActivityFilters {
  kind?: ConsoleActivityKind;
  status?: ConsoleActivityStatus | "";
  q?: string;
  venue_id?: string;
  attention_only?: boolean;
  limit?: number;
  offset?: number;
  /** Passed straight to fetch so polling can be aborted on filter change/unmount. */
  signal?: AbortSignal;
}

export function consoleQuery(params: Record<string, string | number | null | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

const BASE = "/api/v1/admin";

export const consoleApi = {
  home: () => request<ConsoleHomeResponse>(`${BASE}/home`),
  listTournaments: (review: TournamentReviewStatus | "all" = "pending") =>
    request<{ tournaments: ConsoleTournament[]; total: number }>(`${BASE}/tournaments${consoleQuery({ review, limit: 100 })}`),
  reviewTournament: (id: string, body: { decision: "approve" | "reject"; review_note: string | null; expected_updated_at: string }) =>
    request<ConsoleTournament>(`${BASE}/tournaments/${encodeURIComponent(id)}/review`, { method: "POST", body }),
  search: (query: string) => request<ConsoleSearchResponse>(`${BASE}/search${consoleQuery({ q: query })}`),
  listAudit: (filters: AdminAuditFilters = {}) => request<AdminAuditPage>(`${BASE}/audit${consoleQuery({ ...filters })}`),

  listAccounts: (filters: { q?: string; role?: UserRole | ""; limit?: number } = {}) =>
    request<{ accounts: ConsoleAccountSummary[] }>(`${BASE}/accounts${consoleQuery(filters)}`),
  getAccount: (userId: string) => request<ConsoleAccountDetail>(`${BASE}/accounts/${userId}`),
  accountAction: (userId: string, action: "resend-verification" | "password-reset" | "sign-out") =>
    request<ConsoleAccountActionResult>(`${BASE}/accounts/${userId}/${action}`, { method: "POST" }),
  setAccountActive: (userId: string, active: boolean, reason: string) =>
    request<ConsoleAccountActionResult>(`${BASE}/accounts/${userId}/${active ? "reactivate" : "deactivate"}`, {
      method: "POST",
      body: { reason },
    }),

  listReviews: (filters: { venue_id?: string; customer_user_id?: string; status?: VenueReviewStatus | "" } = {}) =>
    request<{ reviews: ConsoleReview[] }>(`${BASE}/reviews${consoleQuery(filters)}`),
  removeReview: (reviewId: string, rationale: string) =>
    request<ConsoleReview>(`${BASE}/reviews/${reviewId}/remove`, { method: "POST", body: { rationale } }),

  listVenues: (filters: { provider_id?: string; status?: VenueApprovalStatus | "" } = {}) =>
    request<{ venues: ConsoleVenueRow[] }>(`${BASE}/console/venues${consoleQuery(filters)}`),
  getVenue: (venueId: string) => request<ConsoleVenueDetail>(`${BASE}/console/venues/${venueId}`),
  takeDownVenue: (venueId: string, reason: string) =>
    request<AdminVenueReview>(`${BASE}/console/venues/${venueId}/take-down`, { method: "POST", body: { reason } }),
  restoreVenue: (venueId: string, reason: string) =>
    request<AdminVenueReview>(`${BASE}/console/venues/${venueId}/restore`, { method: "POST", body: { reason } }),
  createCommissionRule: (
    venueId: string,
    body: { base: CommissionBase; percent: number; effective_from: string; effective_to?: string | null; note?: string | null },
  ) =>
    request<{ rules: ConsoleCommissionRule[] }>(`${BASE}/console/venues/${venueId}/commission-rules`, { method: "POST", body }),
  endCommissionRule: (ruleId: string, effectiveTo: string) =>
    request<{ rules: ConsoleCommissionRule[] }>(`${BASE}/console/commission-rules/${ruleId}/end`, {
      method: "POST",
      body: { effective_to: effectiveTo },
    }),

  settlementOverview: (period: SettlementPeriod = {}) =>
    request<SettlementOverviewResponse>(`${BASE}/settlements${consoleQuery({ ...period })}`),
  providerSettlement: (providerId: string, period: SettlementPeriod = {}) =>
    request<ProviderSettlementResponse>(`${BASE}/providers/${providerId}/settlement${consoleQuery({ ...period })}`),
  issueStatement: (providerId: string, body: { period_start: string; period_end: string; due_date?: string | null; note?: string | null }) =>
    request<ProviderSettlementResponse>(`${BASE}/providers/${providerId}/settlement-statements`, { method: "POST", body }),
  voidStatement: (statementId: string, reason: string) =>
    request<ProviderSettlementResponse>(`${BASE}/settlement-statements/${statementId}/void`, { method: "POST", body: { reason } }),
  recordPayment: (
    providerId: string,
    body: { amount: number; paid_on: string; method: SettlementPaymentMethod; reference?: string | null; note?: string | null; statement_id?: string | null },
    period: SettlementPeriod = {},
  ) =>
    request<ProviderSettlementResponse>(`${BASE}/providers/${providerId}/settlement-payments${consoleQuery({ ...period })}`, {
      method: "POST",
      body,
    }),

  listNotes: (entityType: NoteEntityType, entityId: string) =>
    request<{ notes: ConsoleNote[] }>(`${BASE}/notes${consoleQuery({ entity_type: entityType, entity_id: entityId })}`),
  addNote: (entityType: NoteEntityType, entityId: string, body: string) =>
    request<ConsoleNote>(`${BASE}/notes`, { method: "POST", body: { entity_type: entityType, entity_id: entityId, body } }),

  listActivity: (filters: ConsoleActivityFilters = {}) => {
    const { signal, attention_only, ...rest } = filters;
    return request<ConsoleActivityResponse>(
      `${BASE}/activity${consoleQuery({ kind: "all", ...rest, attention_only: attention_only ? "true" : undefined })}`,
      signal ? { signal } : {},
    );
  },

  /**
   * Booking promotion campaigns. Reads are open to Admin and Support;
   * writes are Admin-only (the server enforces it too). Lists return
   * `{ promotions: [...] }` — never a bare array.
   */
  listPromotions: () =>
    request<{ promotions: PromotionRecord[] }>(`${BASE}/booking-promotions`),
  createPromotion: (body: PromotionConfiguration) =>
    request<PromotionRecord>(`${BASE}/booking-promotions`, { method: "POST", body }),
  getPromotion: (promotionId: string) =>
    request<PromotionRecord>(`${BASE}/booking-promotions/${encodeURIComponent(promotionId)}`),
  revisePromotion: (promotionId: string, body: BookingPromotionRevisionWrite) =>
    request<PromotionRecord>(`${BASE}/booking-promotions/${encodeURIComponent(promotionId)}/revisions`, {
      method: "POST",
      body,
    }),
  setPromotionActive: (promotionId: string, isActive: boolean) =>
    request<PromotionRecord>(`${BASE}/booking-promotions/${encodeURIComponent(promotionId)}`, {
      method: "PATCH",
      body: { is_active: isActive },
    }),
  listRedemptions: (promotionId: string, filters: BookingPromotionRedemptionFilters = {}) =>
    request<BookingPromotionRedemptionsResponse>(
      `${BASE}/booking-promotions/${encodeURIComponent(promotionId)}/redemptions${consoleQuery({
        cursor: filters.cursor ?? undefined,
        limit: filters.limit ?? 50,
      })}`,
    ),

  timeseries: (metric: string, range: string) =>
    request<AdminTimeseries>(`${BASE}/analytics/timeseries${consoleQuery({ metric, range })}`),
  rankings: (dimension: "venue" | "provider", range?: string) =>
    request<AdminRanking>(`${BASE}/analytics/rankings${consoleQuery({ dimension, range })}`),
  funnel: (fromDate: string, toDate: string) =>
    request<AdminFunnel>(`${BASE}/analytics/funnel${consoleQuery({ from_date: fromDate, to_date: toDate })}`),
  /**
   * The analytics page's only call: one network-only lazy snapshot per range.
   * No polling here — the server refreshes the snapshot about hourly and the
   * page re-reads it on load, on range change, or on manual Refresh.
   */
  analyticsReport: (range: ConsoleAnalyticsRange) =>
    request<ConsoleAnalyticsReport>(`${BASE}/analytics/report${consoleQuery({ range })}`),
  /**
   * Booking funnel for a date-range pill. Day ranges keep the explicit
   * from/to bounds; "all" asks for true full history with `?range=all`
   * only — no fabricated epoch date. Existing `funnel()` is untouched.
   */
  funnelForRange: (range: "all" | { days: number }, now: Date = new Date()) =>
    range === "all"
      ? request<AdminFunnel>(`${BASE}/analytics/funnel${consoleQuery({ range: "all" })}`)
      : request<AdminFunnel>(
          `${BASE}/analytics/funnel${consoleQuery({
            from_date: new Date(now.getTime() - range.days * 86_400_000).toISOString(),
            to_date: now.toISOString(),
          })}`,
        ),
};
